import React, { useMemo, useState } from 'react'

export type AppDataTableColumn<T> = {
  name: string
  selector?: (row: T) => React.ReactNode
  cell?: (row: T) => React.ReactNode
  sortable?: boolean
  sortValue?: (row: T) => string | number
  width?: string
  right?: boolean
  grow?: number
}

type AppDataTableProps<T> = {
  columns: AppDataTableColumn<T>[]
  data: T[]
  keyField: (row: T) => string
  progressPending?: boolean
  noDataComponent?: React.ReactNode
  className?: string
}

export function AppDataTable<T>({
  columns,
  data,
  keyField,
  progressPending = false,
  noDataComponent = 'No records found.',
  className = '',
}: AppDataTableProps<T>) {
  const [sortIdx, setSortIdx] = useState<number | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const rows = useMemo(() => {
    if (sortIdx === null) return data
    const col = columns[sortIdx]
    if (!col?.sortable || !col.sortValue) return data
    const copy = [...data]
    copy.sort((a, b) => {
      const av = col.sortValue!(a)
      const bv = col.sortValue!(b)
      if (av < bv) return sortDir === 'asc' ? -1 : 1
      if (av > bv) return sortDir === 'asc' ? 1 : -1
      return 0
    })
    return copy
  }, [columns, data, sortDir, sortIdx])

  const toggleSort = (index: number) => {
    const col = columns[index]
    if (!col?.sortable) return
    if (sortIdx === index) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortIdx(index)
    setSortDir('asc')
  }

  return (
    <div className={`data-table-container ${className}`}>
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th
                key={col.name}
                style={{ width: col.width }}
                className={col.right ? 'text-right' : undefined}
              >
                {col.sortable ? (
                  <button
                    type="button"
                    onClick={() => toggleSort(i)}
                    className="inline-flex items-center gap-1 uppercase tracking-wider"
                  >
                    {col.name}
                    <span className="text-[9px] opacity-70">
                      {sortIdx === i ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  </button>
                ) : (
                  col.name
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {progressPending ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-12">
                <div className="w-8 h-8 border-2 border-[var(--gold-main)] border-t-transparent rounded-full animate-spin mx-auto" />
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-12 text-xs text-[var(--text-muted)]">
                {noDataComponent}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={keyField(row)}>
                {columns.map((col) => (
                  <td key={col.name} className={col.right ? 'text-right' : undefined}>
                    {col.cell ? col.cell(row) : col.selector?.(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
